// src/pages/KarigarManagement/constants/karigar.constant.js

import {
  UserGroupIcon,
  BriefcaseIcon,
  CashIcon,
  ClipboardListIcon,
  CogIcon,
  DocumentReportIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/outline";
import {
  FiUsers,
  FiTool,
  FiDollarSign,
  FiActivity,
  FiCalendar,
  FiClipboard,
  FiPlusCircle,
  FiList,
  FiClock,
} from "react-icons/fi";

// ========================================
// KARIGAR ROUTES
// ========================================
export const KARIGAR_ROUTES = {
  // Root
  ROOT: "/karigar-management",
  DASHBOARD: "/karigar-management",

  // Karigar Management
  KARIGAR_LIST: "/karigar-management/karigars",
  KARIGAR_CREATE: "/karigar-management/karigars/create",
  KARIGAR_DETAIL: "/karigar-management/karigars/:karigarId",
  KARIGAR_EDIT: "/karigar-management/karigars/:karigarId/edit",
  KARIGAR_AVAILABILITY: "/karigar-management/karigars/:karigarId/availability",

  // Job Management
  JOB_LIST: "/karigar-management/jobs",
  JOB_CREATE: "/karigar-management/jobs/create",
  JOB_DETAIL: "/karigar-management/jobs/:jobId",
  JOB_EDIT: "/karigar-management/jobs/:jobId/edit",
  JOB_ASSIGN: "/karigar-management/jobs/:jobId/assign",

  // Payment Management
  PAYMENT_LIST: "/karigar-management/payments",
  PAYMENT_PENDING: "/karigar-management/payments/pending",
  PAYMENT_DETAIL: "/karigar-management/payments/:paymentId",
  KARIGAR_EARNINGS: "/karigar-management/payments/earnings/:karigarId",

  // Future routes (commented for reference)
  // REPORTS: "/karigar-management/reports",
  // ANALYTICS: "/karigar-management/analytics",
  // SETTINGS: "/karigar-management/settings",
};

// ========================================
// KARIGAR PERMISSIONS
// ========================================
export const KARIGAR_PERMISSIONS = {
  // View Permissions
  VIEW_KARIGARS: "VIEW_KARIGARS",
  VIEW_ALL_BRANCH_KARIGARS: "VIEW_ALL_BRANCH_KARIGARS",
  VIEW_KARIGAR_DETAILS: "VIEW_KARIGAR_DETAILS",
  VIEW_KARIGAR_ANALYTICS: "VIEW_KARIGAR_ANALYTICS",
  VIEW_KARIGAR_REPORTS: "VIEW_KARIGAR_REPORTS",

  // Karigar Management Permissions
  CREATE_KARIGAR: "CREATE_KARIGAR",
  EDIT_KARIGAR: "EDIT_KARIGAR",
  DELETE_KARIGAR: "DELETE_KARIGAR",
  MANAGE_KARIGAR_AVAILABILITY: "MANAGE_KARIGAR_AVAILABILITY",
  MANAGE_KARIGAR_SKILLS: "MANAGE_KARIGAR_SKILLS",

  // Job Permissions
  VIEW_JOBS: "VIEW_JOBS",
  CREATE_JOB: "CREATE_JOB",
  EDIT_JOB: "EDIT_JOB",
  DELETE_JOB: "DELETE_JOB",
  ASSIGN_JOB: "ASSIGN_JOB",
  REASSIGN_JOB: "REASSIGN_JOB",
  CANCEL_JOB: "CANCEL_JOB",
  COMPLETE_JOB: "COMPLETE_JOB",

  // Payment Permissions
  VIEW_PAYMENTS: "VIEW_PAYMENTS",
  VIEW_ALL_PAYMENTS: "VIEW_ALL_PAYMENTS",
  CREATE_PAYMENT: "CREATE_PAYMENT",
  APPROVE_PAYMENT: "APPROVE_PAYMENT",
  PROCESS_PAYMENT: "PROCESS_PAYMENT",
  VIEW_EARNINGS: "VIEW_EARNINGS",

  // Admin Permissions
  MANAGE_KARIGAR_SETTINGS: "MANAGE_KARIGAR_SETTINGS",
  ADMIN: "KARIGAR_ADMIN",
};

// ========================================
// KARIGAR STATUS
// ========================================
export const KARIGAR_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  ON_LEAVE: "on_leave",
  SUSPENDED: "suspended",
  PENDING_VERIFICATION: "pending_verification",
  BLACKLISTED: "blacklisted",
};

// ========================================
// JOB STATUS
// ========================================
export const JOB_STATUS = {
  PENDING: "pending",
  ASSIGNED: "assigned",
  IN_PROGRESS: "in_progress",
  ON_HOLD: "on_hold",
  QUALITY_CHECK: "quality_check",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  REJECTED: "rejected",
  REWORK: "rework",
};

// ========================================
// JOB PRIORITY
// ========================================
export const JOB_PRIORITY = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  URGENT: "urgent",
};

// ========================================
// JOB TYPES
// ========================================
export const JOB_TYPES = {
  NEW_MAKING: "new_making",
  REPAIR: "repair",
  ALTERATION: "alteration",
  POLISHING: "polishing",
  RHODIUM_PLATING: "rhodium_plating",
  STONE_SETTING: "stone_setting",
  ENGRAVING: "engraving",
  CUSTOM_ORDER: "custom_order",
  MELTING: "melting",
  CASTING: "casting",
};

// ========================================
// PAYMENT STATUS
// ========================================
export const PAYMENT_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "cancelled",
  ON_HOLD: "on_hold",
};

// ========================================
// PAYMENT TYPES
// ========================================
export const PAYMENT_TYPES = {
  JOB_PAYMENT: "job_payment",
  ADVANCE: "advance",
  BONUS: "bonus",
  DEDUCTION: "deduction",
  SETTLEMENT: "settlement",
};

// ========================================
// KARIGAR SKILL LEVELS
// ========================================
export const SKILL_LEVELS = {
  BEGINNER: "beginner",
  INTERMEDIATE: "intermediate",
  ADVANCED: "advanced",
  EXPERT: "expert",
  MASTER: "master",
};

// ========================================
// STATUS COLORS & BADGES
// ========================================
export const JOB_STATUS_CONFIG = {
  [JOB_STATUS.PENDING]: {
    color: "yellow",
    bgClass: "bg-yellow-100",
    textClass: "text-yellow-800",
    borderClass: "border-yellow-300",
    label: "Pending",
  },
  [JOB_STATUS.ASSIGNED]: {
    color: "blue",
    bgClass: "bg-blue-100",
    textClass: "text-blue-800",
    borderClass: "border-blue-300",
    label: "Assigned",
  },
  [JOB_STATUS.IN_PROGRESS]: {
    color: "indigo",
    bgClass: "bg-indigo-100",
    textClass: "text-indigo-800",
    borderClass: "border-indigo-300",
    label: "In Progress",
  },
  [JOB_STATUS.ON_HOLD]: {
    color: "orange",
    bgClass: "bg-orange-100",
    textClass: "text-orange-800",
    borderClass: "border-orange-300",
    label: "On Hold",
  },
  [JOB_STATUS.QUALITY_CHECK]: {
    color: "purple",
    bgClass: "bg-purple-100",
    textClass: "text-purple-800",
    borderClass: "border-purple-300",
    label: "Quality Check",
  },
  [JOB_STATUS.COMPLETED]: {
    color: "green",
    bgClass: "bg-green-100",
    textClass: "text-green-800",
    borderClass: "border-green-300",
    label: "Completed",
  },
  [JOB_STATUS.CANCELLED]: {
    color: "gray",
    bgClass: "bg-gray-100",
    textClass: "text-gray-800",
    borderClass: "border-gray-300",
    label: "Cancelled",
  },
  [JOB_STATUS.REJECTED]: {
    color: "red",
    bgClass: "bg-red-100",
    textClass: "text-red-800",
    borderClass: "border-red-300",
    label: "Rejected",
  },
  [JOB_STATUS.REWORK]: {
    color: "pink",
    bgClass: "bg-pink-100",
    textClass: "text-pink-800",
    borderClass: "border-pink-300",
    label: "Rework",
  },
};

export const KARIGAR_STATUS_CONFIG = {
  [KARIGAR_STATUS.ACTIVE]: {
    color: "green",
    bgClass: "bg-green-100",
    textClass: "text-green-800",
    label: "Active",
  },
  [KARIGAR_STATUS.INACTIVE]: {
    color: "gray",
    bgClass: "bg-gray-100",
    textClass: "text-gray-800",
    label: "Inactive",
  },
  [KARIGAR_STATUS.ON_LEAVE]: {
    color: "yellow",
    bgClass: "bg-yellow-100",
    textClass: "text-yellow-800",
    label: "On Leave",
  },
  [KARIGAR_STATUS.SUSPENDED]: {
    color: "red",
    bgClass: "bg-red-100",
    textClass: "text-red-800",
    label: "Suspended",
  },
  [KARIGAR_STATUS.PENDING_VERIFICATION]: {
    color: "blue",
    bgClass: "bg-blue-100",
    textClass: "text-blue-800",
    label: "Pending Verification",
  },
  [KARIGAR_STATUS.BLACKLISTED]: {
    color: "red",
    bgClass: "bg-red-900",
    textClass: "text-red-100",
    label: "Blacklisted",
  },
};
